'use client';

import { useCallback, useMemo, useRef } from 'react';
import type { FormConfig, FormInput, FormLeadStatus, FormSubmitPayload } from '@jlg-website/shared';
import { submitLead } from '@/components/pages/form/submit-lead';
import {
  collectQuestionAnswers,
  emitFormSubmit,
  emitLeadSubmitted,
  emitQuestionsFilledOut,
  emitStepProgress,
} from '@/components/pages/form/marketing';
import {
  searchAddress,
  validatePhone,
  validatePostalCode,
} from '@/components/pages/form/field-validators';
import useGoogleConversion from '@/hooks/useGoogleConversion';
import { Plugins } from '@jlg-website/shared';

/**
 * Shared lead-form wiring for the public form (`pages/form/client.tsx`) and the mass-tort hero
 * (`hero-heading/mass-tort/form-client.tsx`): the web-layer field validators, the marketing /
 * analytics events, and the single-step submit path. Both surfaces must fire the exact same
 * events, so this lives once here instead of being copied per client — each client keeps only what
 * differs (its `autoNext` value, initial answers, the hero's redirect hand-off).
 */
export function useLeadForm(schema: FormInput, config?: FormConfig) {
  // Steps already reported, so each `form_progress_*` event fires at most once.
  const emittedSteps = useRef<Set<number>>(new Set());
  const marketingConversion = useGoogleConversion();

  // Inject the web-layer field validators (server actions) the renderer's address, zip-code and
  // phone fields call. Kept on the client so the action references reach the fields without crossing
  // the RSC data boundary as plain props.
  const configWithValidators = useMemo<FormConfig>(
    () => ({ ...config, validators: { validatePhone, validatePostalCode, searchAddress } }),
    [config]
  );

  // Emits `form_submit`, then — for a single-step form that never triggers `onReachLastStep` —
  // fires `emitQuestionsFilledOut` with its answered questions (including any interleaved into the
  // contact block) *before* the lead is submitted. Persisting the lead comes next, followed by the
  // post-submit marketing events with the stored answers and contact.
  const handleSubmit = useCallback(
    async (payload: FormSubmitPayload, plugins: Plugins): Promise<FormLeadStatus> => {
      emitFormSubmit();

      if (schema.form_steps.length === 1) {
        const answers = collectQuestionAnswers(payload);
        if (Object.keys(answers).length > 0) {
          emitQuestionsFilledOut(marketingConversion, payload);
        }
      }

      const result = await submitLead(schema, payload, plugins);
      emitLeadSubmitted(
        marketingConversion,
        result.lead_id,
        result.answers,
        result.contact,
        payload.is_qualified
      );
      return result;
    },
    [schema, marketingConversion]
  );

  const handleStepNext = useCallback(
    (payload: FormSubmitPayload): void => emitStepProgress(emittedSteps.current, payload),
    []
  );

  const handleReachLastStep = useCallback(
    (payload: FormSubmitPayload): void => emitQuestionsFilledOut(marketingConversion, payload),
    [marketingConversion]
  );

  return { config: configWithValidators, handleSubmit, handleStepNext, handleReachLastStep };
}
